import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  Input,
  NgModuleRef,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { ActivatedRoute, Route } from '@angular/router';
import { Subject } from 'rxjs';

import { li } from '../services/logger/logger.service';
import { RenderingContext } from './../../api';
import { ComponentTypeRef } from './../../ng-api';
import { createSingleSubject, isNotNil, KEY_LAYOUT_MODE, KEY_RENDERING_CONTEXT } from './../../utils';

function _getComponentFactoryResolver(
  aConfig?: Route
): ComponentFactoryResolver {
  // check if we have a config
  if (isNotNil(aConfig)) {
    // access the loaded config
    const loadedConfig: any = aConfig['_loadedConfig'];
    if (isNotNil(loadedConfig)) {
      // test if we have a module
      const moduleRef: NgModuleRef<any> = loadedConfig.module;
      if (isNotNil(moduleRef)) {
        // use the resolver
        return moduleRef.componentFactoryResolver;
      }
    }
  }
  // nothing found
  return null;
}

const KEYS = [KEY_LAYOUT_MODE, KEY_RENDERING_CONTEXT];

const LOGGER = 'ContentRefDirective';

@Directive({ selector: '[wchContentRef]' })
export class ContentRefDirective implements OnChanges, OnDestroy {
  @Input() wchContentRef: ComponentTypeRef<any>;

  @Input() cmpResolver: ComponentFactoryResolver;
  /*
   * Rendering context used to render the component reference
   */
  @Input() renderingContext: RenderingContext;

  /*
   * Layout mode used to render the component reference.
   */
  @Input() layoutMode: string;

  /**
   * Output that will be triggered when a new component was created
   */
  @Output() readonly onComponent: Subject<any> = createSingleSubject();

  /**
   * The default component factory resolver
   */
  private readonly _r: ComponentFactoryResolver;

  /**
   * The currently used resolver
   */
  private _cr?: ComponentFactoryResolver;

  /**
   * The currently used type
   */
  private _ct?: ComponentTypeRef<any>;

  /**
   * The currently used component
   */
  private _cc?: ComponentRef<any>;

  constructor(
    private _viewContainerRef: ViewContainerRef,
    _cmpResolver: ComponentFactoryResolver,
    _activatedRoute: ActivatedRoute
  ) {
    // quick hack
    const that = this;
    // get the correct factory
    // TODO is there a better way, we use an internal property for now ...
    this._r =
      _getComponentFactoryResolver(_activatedRoute.routeConfig) || _cmpResolver;
  }

  ngOnChanges(changes: SimpleChanges) {
    // log this
    li(LOGGER, 'ngOnChanges', changes);
    // sanity check
    const that = this;
    // some quick checks
    const viewContainerRef = that._viewContainerRef;
    // get the current resolver
    const type = that.wchContentRef;
    if (isNotNil(type)) {
      // get the resolver
      const resolver = type.resolver || that.cmpResolver || that._r;
      // test if we have changes in the nature of the component
      if (resolver !== that._cr || type !== that._ct) {
        // remove the previous content
        viewContainerRef.clear();
        // check if we have a component at all
        if (isNotNil(resolver) && isNotNil(type)) {
          // log this
          li(LOGGER, 'Creating new component of type ...', type);
          // access the factory
          const fct = resolver.resolveComponentFactory(type.type);
          // create a new component
          const cmp = (that._cc = viewContainerRef.createComponent(fct));
          // emit the new component
          that.onComponent.next(cmp.instance);
        } else {
          // clear
          that._cc = null;
          that.onComponent.next(null);
        }
        // copy the previous references
        that._cr = resolver;
        that._ct = type;
      }
      // update the input values
      const ref = that._cc;
      if (isNotNil(ref)) {
        // the instance
        const inst = ref.instance;
        // update
        const bDirty = KEYS.filter(key => isNotNil(changes[key]))
          .map(key => (inst[key] = changes[key].currentValue))
          .reduce(() => true, false);
        if (bDirty) {
          // log this
          li(LOGGER, 'Marking component as dirty ...', type);
          // mark the component for changes
          const changeDetection = ref.changeDetectorRef;
          if (isNotNil(changeDetection)) {
            // indicates that the component has to be checked
            changeDetection.markForCheck();
          }
        }
      }
    } else {
      // log this
      li(LOGGER, 'No type specified.');
      // remove the previous content
      viewContainerRef.clear();
      // reset all
      that._cc = null;
      that._cr = null;
      that._ct = type;
    }
  }

  ngOnDestroy() {
    // log this
    li(LOGGER, 'ngOnDestroy');
  }
}
